/**
 * The single source for the mockd mark and wordmark (docs/brand.md → Logo usage).
 *
 * Every logo file in the repo is generated from this module by
 * `pnpm --filter @mockd/web generate:brand`, and `MockdLogo.tsx` renders the same
 * glyphs inline. Never hand-edit the generated SVGs or PNGs — change this file and
 * regenerate.
 *
 * Kept import-free and to erasable TypeScript syntax so the generator can load it
 * with Node's built-in type stripping.
 */

/**
 * Logo colours from docs/brand.md. They duplicate tokens.ts because this file must stay
 * import-free for the generator; brandTheme.test.ts fails if the two disagree.
 */
export const LOGO_COLORS = {
  navy: '#1A368B',
  cyan: '#13ECF3',
  ice: '#B8E6FE',
  /** Wordmark `mockd` on a dark background (dark `base-content`). */
  inkOnDark: '#DBEFF9',
  /** Wordmark `mockd` on a light background (light `base-content`). */
  inkOnLight: '#0B183B',
} as const;

/** The `m_` tile: a 64×64 navy square with an ice `m` and a cyan `_`. */
export const MARK = {
  size: 64,
  radius: 8,
  m: 'M15.4245 43V25.4H17.9525V27.512H18.5285L18.0805 28.28C18.0805 27.2987 18.3365 26.52 18.8485 25.944C19.3818 25.368 20.0965 25.08 20.9925 25.08C21.9738 25.08 22.7312 25.4427 23.2645 26.168C23.7978 26.8933 24.0645 27.9173 24.0645 29.24L23.2005 27.512H24.5445L23.9365 28.28C23.9365 27.2987 24.2032 26.52 24.7365 25.944C25.2912 25.368 26.0165 25.08 26.9125 25.08C28.0218 25.08 28.8752 25.496 29.4725 26.328C30.0912 27.16 30.4005 28.2587 30.4005 29.624V43H27.7445V29.592C27.7445 28.8667 27.5845 28.3013 27.2645 27.896C26.9658 27.4907 26.5178 27.288 25.9205 27.288C25.3445 27.288 24.8858 27.4907 24.5445 27.896C24.2245 28.28 24.0645 28.8347 24.0645 29.56V43H21.7605V29.592C21.7605 28.8453 21.5898 28.28 21.2485 27.896C20.9285 27.4907 20.4698 27.288 19.8725 27.288C19.2752 27.288 18.8272 27.4907 18.5285 27.896C18.2298 28.28 18.0805 28.8347 18.0805 29.56V43H15.4245Z',
  cursor: 'M34.42 46.2V43.8H49.78V46.2H34.42Z',
} as const;

/**
 * Wordmark glyphs outlined from JetBrains Mono Medium 2.304 (SIL OFL 1.1), in font
 * units: 1000 per em, y down, baseline at y = 0. Each glyph sits in its own
 * advance cell starting at x = 0.
 */
export const GLYPH_UNITS_PER_EM = 1000;
export const GLYPH_ADVANCE = 600;

export const GLYPHS = {
  m: 'M57 0L57 -550L141 -550L141 -475L144 -475Q148 -513 173 -536Q197 -560 235 -560Q272 -560 297 -538Q322 -516 333 -479Q339 -517 364 -538Q389 -560 427 -560Q479 -560 511 -520Q543 -481 543 -418L543 0L453 0L453 -417Q453 -448 438 -466Q422 -485 396 -485Q370 -485 355 -467Q340 -449 340 -417L340 0L260 0L260 -417Q260 -448 245 -466Q230 -485 204 -485Q178 -485 163 -467Q147 -449 147 -417L147 0Z',
  o: 'M300 8Q199 8 140 -50Q80 -108 80 -212L80 -338Q80 -442 140 -500Q199 -558 300 -558Q401 -558 461 -500Q520 -442 520 -338L520 -212Q520 -108 461 -50Q401 8 300 8ZM300 -79Q356 -79 389 -110Q421 -142 421 -203L421 -347Q421 -408 389 -439Q356 -471 300 -471Q244 -471 212 -439Q179 -408 179 -347L179 -203Q179 -142 212 -110Q244 -79 300 -79Z',
  c: 'M308 10Q207 10 146 -46Q85 -103 85 -202L85 -349Q85 -447 146 -503Q207 -560 308 -560Q405 -560 465 -508Q524 -457 527 -368L429 -368Q426 -418 394 -445Q361 -473 308 -473Q251 -473 218 -440Q184 -408 184 -349L184 -202Q184 -142 218 -110Q251 -78 308 -78Q362 -78 394 -105Q426 -132 429 -182L527 -182Q524 -93 465 -41Q405 10 308 10Z',
  k: 'M93 0L93 -730L192 -730L192 -326L293 -326L441 -550L553 -550L380 -287L562 0L448 0L294 -241L192 -241L192 0Z',
  d: 'M264 10Q181 10 132 -45Q83 -101 83 -196L83 -354Q83 -449 132 -504Q181 -560 264 -560Q325 -560 366 -529Q406 -498 415 -444L413 -572L413 -730L512 -730L512 0L414 0L414 -107Q405 -52 365 -21Q325 10 264 10ZM298 -76Q351 -76 382 -109Q413 -142 413 -202L413 -348Q413 -408 382 -441Q351 -474 298 -474Q243 -474 213 -445Q182 -417 182 -353L182 -197Q182 -133 213 -104Q243 -76 298 -76Z',
  _: 'M60 110L60 25L540 25L540 110Z',
} as const;

export type LogoGlyph = keyof typeof GLYPHS;

export interface PlacedGlyph {
  char: LogoGlyph;
  d: string;
  /** Left edge of the glyph's advance cell, in font units. */
  x: number;
}

/** Lays `text` out on one baseline, one advance cell per character. */
export function layoutGlyphs(text: string): PlacedGlyph[] {
  return [...text].map((char, i) => {
    if (!(char in GLYPHS)) throw new Error(`No logo glyph for "${char}"`);
    const glyph = char as LogoGlyph;
    return { char: glyph, d: GLYPHS[glyph], x: i * GLYPH_ADVANCE };
  });
}

/** Ink bounds of `mockd_`: `m` left side bearing to `_` right edge, ascender to `_` bottom. */
export const WORDMARK_BOUNDS = { x: 57, y: -730, width: 3483, height: 840 } as const;

export type WordmarkVariant = 'on-dark' | 'on-light' | 'mono';

const WORDMARK_FILLS: Record<WordmarkVariant, { ink: string; cursor: string }> = {
  'on-dark': { ink: LOGO_COLORS.inkOnDark, cursor: LOGO_COLORS.cyan },
  'on-light': { ink: LOGO_COLORS.inkOnLight, cursor: LOGO_COLORS.navy },
  mono: { ink: 'currentColor', cursor: 'currentColor' },
};

const XMLNS = 'xmlns="http://www.w3.org/2000/svg"';

function glyphPaths(text: string, indent: string): string {
  return layoutGlyphs(text)
    .map(({ d, x }) => `${indent}<path transform="translate(${x} 0)" d="${d}"/>`)
    .join('\n');
}

/** The wordmark's glyphs as SVG elements, ink in one group and the cursor in another. */
function wordmarkBody(variant: WordmarkVariant, indent: string): string {
  const { ink, cursor } = WORDMARK_FILLS[variant];
  return [
    `${indent}<g fill="${ink}">`,
    glyphPaths('mockd', `${indent}  `),
    `${indent}</g>`,
    `${indent}<path fill="${cursor}" transform="translate(${5 * GLYPH_ADVANCE} 0)" d="${GLYPHS._}"/>`,
  ].join('\n');
}

const round = (n: number) => Math.round(n * 100) / 100;

/** The mark: navy tile, ice `m`, cyan `_`. Same in both themes. */
export function markSvg(): string {
  const { size, radius, m, cursor } = MARK;
  return `<svg ${XMLNS} width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
  <title>mockd</title>
  <rect width="${size}" height="${size}" rx="${radius}" fill="${LOGO_COLORS.navy}"/>
  <path fill="${LOGO_COLORS.ice}" d="${m}"/>
  <path fill="${LOGO_COLORS.cyan}" d="${cursor}"/>
</svg>
`;
}

/** One-colour mark: a `currentColor` tile with the glyphs knocked out. */
export function markMonoSvg(): string {
  const { size: s, radius: r, m, cursor } = MARK;
  const tile = `M${r} 0H${s - r}A${r} ${r} 0 0 1 ${s} ${r}V${s - r}A${r} ${r} 0 0 1 ${s - r} ${s}H${r}A${r} ${r} 0 0 1 0 ${s - r}V${r}A${r} ${r} 0 0 1 ${r} 0Z`;
  return `<svg ${XMLNS} width="${s}" height="${s}" viewBox="0 0 ${s} ${s}">
  <title>mockd</title>
  <path fill="currentColor" fill-rule="evenodd" d="${tile}${m}${cursor}"/>
</svg>
`;
}

/**
 * Full-bleed source for maskable and Apple touch icons: square navy field, no
 * rounded corners (the platform applies its own mask). The glyphs keep their mark
 * positions, which lie within the central 80% safe zone.
 */
export function markMaskableSvg(): string {
  const { size, m, cursor } = MARK;
  return `<svg ${XMLNS} width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
  <title>mockd</title>
  <rect width="${size}" height="${size}" fill="${LOGO_COLORS.navy}"/>
  <path fill="${LOGO_COLORS.ice}" d="${m}"/>
  <path fill="${LOGO_COLORS.cyan}" d="${cursor}"/>
</svg>
`;
}

/** Display height of the standalone wordmark files, in CSS px. */
const WORDMARK_HEIGHT = 48;

/** `mockd_` cropped to its ink bounds. */
export function wordmarkSvg(variant: WordmarkVariant): string {
  const { x, y, width, height } = WORDMARK_BOUNDS;
  const displayWidth = round((width / height) * WORDMARK_HEIGHT);
  return `<svg ${XMLNS} width="${displayWidth}" height="${WORDMARK_HEIGHT}" viewBox="${x} ${y} ${width} ${height}">
  <title>mockd</title>
${wordmarkBody(variant, '  ')}
</svg>
`;
}

/** Wordmark tile geometry, in font units. Corner radius follows the mark (8/64 of height). */
export const WORDMARK_TILE = { width: 4200, height: 1440, displayWidth: 280, displayHeight: 96 } as const;

/**
 * The on-dark wordmark on its own navy tile, for surfaces that may invert colours
 * (email clients' dark modes). Exported as a PNG by the generator.
 */
export function wordmarkTileSvg(): string {
  const { width, height, displayWidth, displayHeight } = WORDMARK_TILE;
  const b = WORDMARK_BOUNDS;
  const tx = round((width - b.width) / 2 - b.x);
  const ty = round((height - b.height) / 2 - b.y);
  return `<svg ${XMLNS} width="${displayWidth}" height="${displayHeight}" viewBox="0 0 ${width} ${height}">
  <title>mockd</title>
  <rect width="${width}" height="${height}" rx="${(height * MARK.radius) / MARK.size}" fill="${LOGO_COLORS.navy}"/>
  <g transform="translate(${tx} ${ty})">
${wordmarkBody('on-dark', '    ')}
  </g>
</svg>
`;
}

/** The canonical files in `packages/shared/brand/`, by name. */
export const BRAND_SVG_FILES: Record<string, () => string> = {
  'mark.svg': markSvg,
  'mark-mono.svg': markMonoSvg,
  'mark-maskable.svg': markMaskableSvg,
  'wordmark-on-dark.svg': () => wordmarkSvg('on-dark'),
  'wordmark-on-light.svg': () => wordmarkSvg('on-light'),
  'wordmark-mono.svg': () => wordmarkSvg('mono'),
  'wordmark-tile.svg': wordmarkTileSvg,
};
